<template>
  <div class="shell">
    <div class="bg-grid" aria-hidden="true" />
    <header class="topbar">
      <div class="brand">
        <div class="logo-wrap">
          <span class="logo">◐</span>
        </div>
        <div>
          <h1>{{ appName }}</h1>
          <p class="sub">{{ appSubtitle }} · v{{ appVersion }}</p>
        </div>
      </div>
      <div class="topbar-right">
        <div class="rating-pill" title="赛季 Rating">
          <span class="rating-label">Rating</span>
          <strong>{{ playerRating }}</strong>
        </div>
        <button
          v-if="phase === 'playing'"
          type="button"
          class="btn ghost"
          @click="confirmLeave"
        >
          退出
        </button>
      </div>
    </header>

    <main class="main">
      <LobbyView
        v-if="phase === 'lobby'"
        v-model:board-size="boardSizeInput"
        v-model:display-name="displayName"
        v-model:match-region="matchRegion"
        :board-sizes="boardSizes"
        :rating="playerRating"
        :valid-size="validSize"
        :can-match="canMatch"
        :daily-bonus-notice="dailyBonusNotice"
        @match="startMatch"
        @random-name="randomDisplayName"
        @persist-profile="persistProfile"
      />

      <MatchingView
        v-else-if="phase === 'matching'"
        :label="matchLabel"
        :server="matchMeta.server"
        :ping-ms="matchMeta.pingMs"
        :step-index="matchStepIndex"
        :progress="matchProgress"
        :you-name="displayName"
        :you-rating="playerRating"
        :opponent="pendingOpponent"
      />

      <section v-else-if="phase === 'playing'" class="play-zone">
        <div class="room-bar">
          <span class="room-id">房间 {{ roomId }}</span>
          <span class="room-dot" :class="{ live: !negotiationBusy }">LIVE</span>
          <span v-if="undoUsed < maxUndo" class="room-hint">悔棋剩余 {{ maxUndo - undoUsed }}</span>
        </div>

        <div class="play-grid">
          <aside class="panel players">
            <div
              class="player-row"
              :class="{ active: currentPlayer === BLACK, local: localColor === BLACK }"
            >
              <span
                class="avatar"
                :style="{ background: blackPlayer.avatarColor }"
              >
                {{ avatarLetter(blackPlayer.name) }}
              </span>
              <div class="player-info">
                <div class="pname">
                  {{ blackPlayer.name }}
                  <span class="chip black">黑</span>
                  <span v-if="localColor === BLACK" class="chip you">我</span>
                </div>
                <div class="pmeta">Rating {{ blackPlayer.rating }}</div>
              </div>
              <span class="disc-count">{{ counts.black }}</span>
            </div>

            <div class="turn-banner">
              <template v-if="negotiationBusy">
                <span class="pulse-text">等待对手回应…</span>
              </template>
              <template v-else-if="opponentThinking">
                <span class="pulse-text">对手思考中</span>
                <span class="timer-fake">{{ thinkingDots }}</span>
              </template>
              <template v-else>
                <span>{{ statusMessage }}</span>
              </template>
            </div>

            <div
              class="player-row"
              :class="{ active: currentPlayer === WHITE, local: localColor === WHITE }"
            >
              <span
                class="avatar"
                :style="{ background: whitePlayer.avatarColor }"
              >
                {{ avatarLetter(whitePlayer.name) }}
              </span>
              <div class="player-info">
                <div class="pname">
                  {{ whitePlayer.name }}
                  <span class="chip white">白</span>
                  <span v-if="localColor === WHITE" class="chip you">我</span>
                </div>
                <div class="pmeta">
                  Rating {{ whitePlayer.rating }}
                  <span v-if="opponentPing"> · {{ opponentPing }}ms</span>
                </div>
              </div>
              <span class="disc-count">{{ counts.white }}</span>
            </div>

            <div class="actions">
              <button
                type="button"
                class="btn action"
                :disabled="!canRequestDraw"
                @click="requestDraw"
              >
                求和
              </button>
              <button
                type="button"
                class="btn action"
                :disabled="!canRequestUndo"
                @click="requestUndo"
              >
                悔棋
              </button>
              <button
                type="button"
                class="btn action danger"
                :disabled="gameOver || negotiationBusy"
                @click="requestResign"
              >
                认输
              </button>
              <button
                v-if="canPass"
                type="button"
                class="btn action muted"
                @click="doPass"
              >
                跳过
              </button>
            </div>
          </aside>

          <div class="board-col">
            <OthelloBoard
              :board="board"
              :legal-moves="visibleLegalMoves"
              :interactive="boardInteractive"
              :last-move="lastMove"
              @move="onLocalMove"
            />
          </div>

          <aside class="panel chat">
            <div class="chat-head">对局频道</div>
            <ul ref="chatList" class="chat-log">
              <li
                v-for="(msg, i) in chatMessages"
                :key="i"
                :class="msg.type"
              >
                <span v-if="msg.type === 'system'" class="sys">{{ msg.text }}</span>
                <span v-else-if="msg.type === 'opp'" class="opp">
                  <strong>{{ opponentName }}:</strong> {{ msg.text }}
                </span>
                <span v-else class="me"><strong>我:</strong> {{ msg.text }}</span>
              </li>
            </ul>
          </aside>
        </div>
      </section>

      <section v-else-if="phase === 'ended'" class="card end-card">
        <div class="end-icon" :class="endOutcome">{{ endEmoji }}</div>
        <h2>{{ resultTitle }}</h2>
        <p class="end-sub">{{ resultDetail }}</p>
        <p class="end-score">{{ counts.black }} : {{ counts.white }}</p>
        <p v-if="ratingDelta !== null" class="rating-change">
          Rating {{ ratingBefore }} → {{ playerRating }}
          <span :class="ratingDelta >= 0 ? 'up' : 'down'">
            ({{ ratingDelta >= 0 ? '+' : '' }}{{ ratingDelta }})
          </span>
        </p>
        <button type="button" class="btn primary" @click="backToLobby">
          返回大厅
        </button>
      </section>
    </main>

    <div v-if="modal" class="modal-backdrop" @click.self="closeModal">
      <div class="modal">
        <h3>{{ modal.title }}</h3>
        <p>{{ modal.body }}</p>
        <div class="modal-actions">
          <button type="button" class="btn ghost" @click="closeModal">
            取消
          </button>
          <button type="button" class="btn primary" @click="confirmModal">
            确定
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import OthelloBoard from './OthelloBoard.vue'
import LobbyView from './LobbyView.vue'
import MatchingView from './MatchingView.vue'
import gameConfig, { getDefaultBoardSize } from '../config/gameConfig'
import { generatePlayerDisplayName } from '../game/nicknames'
import {
  BLACK,
  WHITE,
  OPPONENT,
  ALLOWED_BOARD_SIZES,
  isValidBoardSize,
} from '../game/constants'
import {
  createBoard,
  cloneBoard,
  getLegalMoves,
  applyMove,
  hasAnyLegalMove,
  countPieces,
} from '../game/board'
import { chooseAiMove, pickHumanLikeDelay } from '../game/ai'
import {
  createLocalProfile,
  runMatchmaking,
  assignSides,
  formatMatchRoomId,
  pickDefaultMatchRegion,
} from '../game/session'
import {
  loadRating,
  chargeMatchEntry,
  applyMatchWinBonus,
  loadProfile,
  saveProfile,
  applyDailyLoginBonusIfEligible,
} from '../game/rating'
import {
  resolveDrawRequest,
  resolveUndoRequest,
  delayForNegotiation,
  maxUndosPerGame,
  shouldAiRequestDraw,
  maybeIdleOpponentChat,
} from '../game/negotiation'
export default {
  name: 'GameShell',
  components: { OthelloBoard, LobbyView, MatchingView },
  data() {
    const profile = loadProfile()
    const savedName = profile.displayName
    const savedRegion =
      profile.matchRegion && gameConfig.matchmaking.regions?.includes(profile.matchRegion)
        ? profile.matchRegion
        : pickDefaultMatchRegion()
    return {
      BLACK,
      WHITE,
      appName: gameConfig.app.name,
      appSubtitle: gameConfig.app.subtitle,
      appVersion: gameConfig.app.version,
      boardSizes: [...ALLOWED_BOARD_SIZES],
      boardSizeInput: getDefaultBoardSize(),
      displayName: savedName || generatePlayerDisplayName(),
      matchRegion: savedRegion,
      dailyBonusNotice: '',
      playerRating: loadRating(),
      phase: 'lobby',
      matchLabel: '',
      matchStepIndex: 0,
      matchProgress: 8,
      matchMeta: {},
      pendingOpponent: null,
      roomId: '',
      board: [],
      size: 8,
      currentPlayer: BLACK,
      localColor: BLACK,
      blackPlayer: null,
      whitePlayer: null,
      legalMoves: [],
      lastMove: null,
      moveCount: 0,
      opponentThinking: false,
      statusMessage: '',
      gameOver: false,
      endReason: 'normal',
      aiTimer: null,
      negTimer: null,
      negotiationBusy: false,
      history: [],
      undoUsed: 0,
      maxUndo: maxUndosPerGame(),
      chatMessages: [],
      modal: null,
      modalAction: null,
      ratingBefore: null,
      ratingDelta: null,
      /** 本局是否已扣入场分（匹配开始时） */
      matchEntryCharged: false,
      /** 扣入场分前的 Rating，用于结算展示 */
      ratingAtMatchStart: null,
      thinkingTick: 0,
      thinkingInterval: null,
      opponentPing: null,
    }
  },
  computed: {
    canMatch() {
      const min = gameConfig.rating.minScoreToMatch ?? gameConfig.rating.entryFee
      return this.playerRating >= min
    },
    validSize() {
      return isValidBoardSize(this.boardSizeInput)
    },
    counts() {
      if (!this.board.length) return { black: 0, white: 0 }
      return countPieces(this.board, this.size)
    },
    isLocalTurn() {
      return this.currentPlayer === this.localColor
    },
    opponentName() {
      const opp = this.localColor === BLACK ? this.whitePlayer : this.blackPlayer
      return opp?.name || '对手'
    },
    boardInteractive() {
      return (
        this.isLocalTurn &&
        !this.opponentThinking &&
        !this.gameOver &&
        !this.negotiationBusy
      )
    },
    visibleLegalMoves() {
      if (!this.boardInteractive) return []
      return this.legalMoves
    },
    canPass() {
      return (
        this.phase === 'playing' &&
        this.isLocalTurn &&
        !this.opponentThinking &&
        !this.negotiationBusy &&
        this.legalMoves.length === 0 &&
        !this.gameOver
      )
    },
    canRequestDraw() {
      return (
        this.phase === 'playing' &&
        !this.gameOver &&
        !this.negotiationBusy &&
        !this.opponentThinking &&
        this.moveCount >= 4
      )
    },
    canRequestUndo() {
      return (
        this.phase === 'playing' &&
        !this.gameOver &&
        !this.negotiationBusy &&
        !this.opponentThinking &&
        this.history.length >= 2 &&
        this.undoUsed < this.maxUndo
      )
    },
    endOutcome() {
      if (this.endReason === 'resign') return 'resign'
      if (this.endReason === 'draw') return 'draw'
      const { black, white } = this.counts
      if (black === white) return 'draw'
      const win =
        (this.localColor === BLACK && black > white) ||
        (this.localColor === WHITE && white > black)
      return win ? 'win' : 'loss'
    },
    endEmoji() {
      const m = { win: '🏆', loss: '😔', draw: '🤝', resign: '🏳️' }
      return m[this.endOutcome] || '◐'
    },
    resultTitle() {
      if (this.endReason === 'draw') return '和棋'
      if (this.endReason === 'resign') return '已认输'
      const { black, white } = this.counts
      if (black === white) return '平局'
      const localWins =
        (this.localColor === BLACK && black > white) ||
        (this.localColor === WHITE && white > black)
      return localWins ? '胜利' : '惜败'
    },
    resultDetail() {
      if (this.endReason === 'draw') return `与 ${this.opponentName} 协商和棋`
      if (this.endReason === 'leave') return '你已退出，本局判负'
      if (this.endReason === 'resign') return '对局因认输结束'
      return `与 ${this.opponentName} 的对局结束`
    },
    thinkingDots() {
      return '.'.repeat((this.thinkingTick % 3) + 1)
    },
  },
  mounted() {
    this.applyLobbyLoginBonus()
    if (!loadProfile().displayName) {
      this.persistProfile()
    }
  },
  watch: {
    matchRegion() {
      this.persistProfile()
    },
  },
  beforeUnmount() {
    this.clearAiTimer()
    this.clearNegTimer()
    if (this.thinkingInterval) clearInterval(this.thinkingInterval)
  },
  methods: {
    avatarLetter(name) {
      return (name || '?').slice(0, 1).toUpperCase()
    },
    applyLobbyLoginBonus() {
      const { granted, amount, score } = applyDailyLoginBonusIfEligible()
      this.playerRating = score
      this.dailyBonusNotice = granted
        ? `今日登录补给已发放 +${amount} 分，当前 ${score} 分`
        : ''
    },
    persistProfile() {
      const prev = loadProfile()
      saveProfile({
        ...prev,
        displayName: this.displayName || generatePlayerDisplayName(),
        matchRegion: this.matchRegion,
      })
    },
    randomDisplayName() {
      this.displayName = generatePlayerDisplayName()
      this.persistProfile()
    },
    pushChat(text, type = 'system') {
      this.chatMessages.push({ text, type, t: Date.now() })
      this.$nextTick(() => {
        const el = this.$refs.chatList
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    pushSystem(text) {
      this.pushChat(text, 'system')
    },
    snapshot() {
      return {
        board: cloneBoard(this.board),
        currentPlayer: this.currentPlayer,
        lastMove: this.lastMove ? [...this.lastMove] : null,
        moveCount: this.moveCount,
      }
    },
    restoreSnapshot(snap) {
      this.board = cloneBoard(snap.board)
      this.currentPlayer = snap.currentPlayer
      this.lastMove = snap.lastMove
      this.moveCount = snap.moveCount
      this.refreshLegals()
    },
    clearAiTimer() {
      if (this.aiTimer) {
        clearTimeout(this.aiTimer)
        this.aiTimer = null
      }
    },
    clearNegTimer() {
      if (this.negTimer) {
        clearTimeout(this.negTimer)
        this.negTimer = null
      }
    },
    startThinkingAnim() {
      if (this.thinkingInterval) return
      this.thinkingInterval = setInterval(() => {
        this.thinkingTick += 1
      }, 400)
    },
    stopThinkingAnim() {
      if (this.thinkingInterval) {
        clearInterval(this.thinkingInterval)
        this.thinkingInterval = null
      }
    },
    closeModal() {
      this.modal = null
      this.modalAction = null
    },
    confirmModal() {
      const action = this.modalAction
      this.closeModal()
      if (action === 'leave') {
        this.finishGame('loss', 'leave')
      } else if (action === 'resign') {
        this.finishGame('loss', 'resign')
      }
    },
    confirmLeave() {
      this.modal = {
        title: '退出对局',
        body: '确定退出？将判负，入场分已扣除，不会额外扣分。',
      }
      this.modalAction = 'leave'
    },
    backToLobby() {
      this.phase = 'lobby'
      this.gameOver = false
      this.ratingDelta = null
      this.ratingAtMatchStart = null
      this.matchEntryCharged = false
      this.applyLobbyLoginBonus()
    },
    startMatch() {
      if (!this.validSize || !this.canMatch) return
      this.persistProfile()
      const entry = chargeMatchEntry()
      if (!entry.ok) return
      this.ratingAtMatchStart = entry.before
      this.matchEntryCharged = true
      this.playerRating = entry.after
      this.phase = 'matching'
      this.pendingOpponent = null
      this.matchMeta = {}
      this.matchLabel = '正在接入匹配服务…'
      this.matchStepIndex = 0
      this.matchProgress = 8
      runMatchmaking(
        (ev) => {
          this.matchLabel = ev.label
          this.matchStepIndex = ev.stepIndex ?? 0
          this.matchProgress = ev.progress ?? 20
          this.matchMeta = { server: ev.server, pingMs: ev.pingMs }
          if (ev.opponent) this.pendingOpponent = ev.opponent
        },
        entry.before,
        this.matchRegion
      ).then((opponent) => {
        this.initGame(opponent)
      })
    },
    initGame(opponent) {
      const local = createLocalProfile(
        this.displayName || '棋手',
        this.ratingAtMatchStart ?? this.playerRating,
        this.matchRegion
      )
      const sides = assignSides(local, opponent)
      this.blackPlayer = sides.black
      this.whitePlayer = sides.white
      this.localColor = sides.localColor
      this.opponentPing = opponent.pingMs
      this.roomId = formatMatchRoomId()
      this.size = this.boardSizeInput
      this.board = createBoard(this.size)
      this.currentPlayer = BLACK
      this.lastMove = null
      this.moveCount = 0
      this.gameOver = false
      this.endReason = 'normal'
      this.opponentThinking = false
      this.negotiationBusy = false
      this.history = [this.snapshot()]
      this.undoUsed = 0
      this.chatMessages = []
      this.phase = 'playing'
      this.refreshLegals()
      const side = this.localColor === BLACK ? '黑' : '白'
      this.pushSystem(
        gameConfig.disguise.systemLines.joined.replace('{name}', opponent.name)
      )
      this.pushSystem(`你执${side}。${this.isLocalTurn ? '你先手。' : '对手先手。'}`)
      this.statusMessage = this.isLocalTurn ? '轮到你了' : '等待对手…'
      this.scheduleAiIfNeeded()
    },
    refreshLegals() {
      this.legalMoves = getLegalMoves(this.board, this.size, this.currentPlayer)
    },
    finishGame(outcome, reason = 'normal') {
      this.gameOver = true
      this.endReason = reason === 'normal' && outcome === 'draw' ? 'draw' : reason
      this.clearAiTimer()
      this.clearNegTimer()
      this.stopThinkingAnim()
      this.opponentThinking = false
      this.negotiationBusy = false

      const base =
        this.ratingAtMatchStart != null
          ? this.ratingAtMatchStart
          : loadRating() + gameConfig.rating.entryFee

      if (outcome === 'win') {
        this.playerRating = applyMatchWinBonus()
      } else {
        this.playerRating = loadRating()
      }

      this.ratingBefore = base
      this.ratingDelta = this.playerRating - base
      this.phase = 'ended'
    },
    checkEndOrPass() {
      if (this.legalMoves.length > 0) {
        this.statusMessage = this.isLocalTurn ? '轮到你了' : '等待对手…'
        return false
      }
      const opp = OPPONENT[this.currentPlayer]
      if (hasAnyLegalMove(this.board, this.size, opp)) {
        this.statusMessage =
          this.currentPlayer === this.localColor
            ? '无合法着法，请跳过'
            : '对手将跳过回合'
        this.currentPlayer = opp
        this.refreshLegals()
        this.scheduleAiIfNeeded()
        return false
      }
      const { black, white } = this.counts
      let outcome = 'draw'
      if (black !== white) {
        const localWins =
          (this.localColor === BLACK && black > white) ||
          (this.localColor === WHITE && white > black)
        outcome = localWins ? 'win' : 'loss'
      }
      this.finishGame(outcome, 'normal')
      return true
    },
    doPass() {
      if (!this.canPass) return
      const opp = OPPONENT[this.currentPlayer]
      this.currentPlayer = opp
      this.refreshLegals()
      this.scheduleAiIfNeeded()
    },
    onLocalMove(r, c) {
      if (!this.boardInteractive) return
      if (!this.legalMoves.some(([rr, cc]) => rr === r && cc === c)) return
      this.applyMove(r, c, this.localColor, true)
    },
    applyMove(r, c, player, recordHistory = false) {
      if (recordHistory) {
        this.history.push(this.snapshot())
      }
      this.board = applyMove(this.board, this.size, r, c, player)
      this.lastMove = [r, c]
      this.moveCount += 1
      this.currentPlayer = OPPONENT[player]
      this.refreshLegals()
      if (this.checkEndOrPass()) return
      this.scheduleAiIfNeeded()
      this.maybeAiDrawAfterMove()
    },
    maybeAiDrawAfterMove() {
      if (this.gameOver || this.negotiationBusy) return
      if (!shouldAiRequestDraw(this.moveCount)) return
      this.negotiationBusy = true
      this.pushChat('要不和棋？', 'opp')
      const delay = delayForNegotiation('draw')
      this.negTimer = setTimeout(() => {
        this.negTimer = null
        this.negotiationBusy = false
        const accept =
          Math.random() <
          gameConfig.negotiation.aiProactiveDraw.localAcceptProbability
        if (accept) {
          this.pushChat('可以，和吧。', 'me')
          this.pushSystem('双方和棋')
          this.finishGame('draw', 'draw')
        } else {
          this.pushChat('再下会儿。', 'me')
        }
      }, delay)
    },
    requestDraw() {
      if (!this.canRequestDraw) return
      this.negotiationBusy = true
      this.pushChat('请求和棋', 'me')
      const delay = delayForNegotiation('draw')
      this.negTimer = setTimeout(() => {
        this.negTimer = null
        const { accepted, message } = resolveDrawRequest()
        this.pushChat(message, 'opp')
        this.negotiationBusy = false
        if (accepted) {
          this.pushSystem('双方和棋')
          this.finishGame('draw', 'draw')
        }
      }, delay)
    },
    requestUndo() {
      if (!this.canRequestUndo) return
      this.negotiationBusy = true
      this.pushChat('能悔棋吗？', 'me')
      const delay = delayForNegotiation('undo')
      this.negTimer = setTimeout(() => {
        this.negTimer = null
        const { accepted, message } = resolveUndoRequest()
        this.pushChat(message, 'opp')
        this.negotiationBusy = false
        if (accepted && this.history.length >= 2) {
          this.history.pop()
          const snap = this.history[this.history.length - 1]
          this.restoreSnapshot(snap)
          this.undoUsed += 1
          this.pushSystem('已悔棋，重赛当前局面')
          this.statusMessage = this.isLocalTurn ? '轮到你了' : '等待对手…'
          this.scheduleAiIfNeeded()
        }
      }, delay)
    },
    requestResign() {
      this.modal = {
        title: '认输',
        body: gameConfig.negotiation.resign.confirmText,
      }
      this.modalAction = 'resign'
    },
    scheduleAiIfNeeded() {
      this.clearAiTimer()
      if (this.gameOver || this.phase !== 'playing' || this.negotiationBusy) return
      const aiColor = this.localColor === BLACK ? WHITE : BLACK
      if (this.currentPlayer !== aiColor) return

      this.opponentThinking = true
      this.statusMessage = ''
      this.startThinkingAnim()
      const legalCount = this.legalMoves.length
      const delay = pickHumanLikeDelay(this.size, this.moveCount, legalCount)
      this.aiTimer = setTimeout(() => {
        this.aiTimer = null
        const chat = maybeIdleOpponentChat()
        if (chat) this.pushChat(chat, 'opp')
        const move = chooseAiMove(this.board, this.size, aiColor)
        this.opponentThinking = false
        this.stopThinkingAnim()
        if (move) {
          this.applyMove(move[0], move[1], aiColor, true)
        } else {
          this.refreshLegals()
          this.checkEndOrPass()
        }
      }, delay)
    },
  },
}
</script>

<style scoped>
.shell {
  min-height: 100vh;
  position: relative;
  color: #e8eef7;
  font-family: 'Segoe UI', 'PingFang SC', system-ui, sans-serif;
  background: #0a0e17;
}

.bg-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse at 50% 0%, black 20%, transparent 75%);
  pointer-events: none;
}

.topbar {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 1rem 1.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(12px);
  background: rgba(10, 14, 23, 0.75);
}

.brand {
  grid-column: 2;
  justify-self: center;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.logo-wrap {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, #10b981, #059669);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.35);
}

.logo {
  font-size: 1.6rem;
  color: #fff;
}

.brand h1 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.sub {
  margin: 0.2rem 0 0;
  font-size: 0.72rem;
  color: #64748b;
}

.topbar-right {
  grid-column: 3;
  justify-self: end;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.rating-pill {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.45rem 1rem;
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.12);
  border: 1px solid rgba(16, 185, 129, 0.35);
}

.rating-label {
  font-size: 0.75rem;
  color: #6ee7b7;
}

.rating-pill strong {
  font-size: 1.15rem;
  color: #ecfdf5;
}

.main {
  position: relative;
  z-index: 1;
  max-width: 1180px;
  margin: 0 auto;
  padding: 1.75rem 1.25rem 3rem;
}

.card {
  background: rgba(17, 24, 39, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.35);
}

.lobby-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2.5rem;
  align-items: center;
}

@media (max-width: 768px) {
  .lobby-grid {
    grid-template-columns: 1fr;
  }
}

.lobby-hero h2 {
  margin: 0 0 0.75rem;
  font-size: 1.75rem;
}

.hero-text {
  color: #94a3b8;
  line-height: 1.6;
}

.feature-list {
  margin: 1.25rem 0 0;
  padding-left: 1.2rem;
  color: #cbd5e1;
  line-height: 1.9;
}

.field {
  display: block;
  margin-bottom: 1.25rem;
}

.field span {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  color: #94a3b8;
}

.field input[type='text'] {
  width: 100%;
  padding: 0.65rem 0.85rem;
  border-radius: 10px;
  border: 1px solid #334155;
  background: #0f172a;
  color: #f1f5f9;
}

.size-control {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.size-control input[type='range'] {
  flex: 1;
}

.size-badge {
  min-width: 4.5rem;
  text-align: center;
  padding: 0.35rem 0.6rem;
  border-radius: 8px;
  background: #1e293b;
  font-weight: 600;
}

.btn {
  border: none;
  border-radius: 10px;
  padding: 0.55rem 1.1rem;
  font-size: 0.9rem;
  cursor: pointer;
  transition: transform 0.12s, opacity 0.12s;
}

.btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.btn:not(:disabled):active {
  transform: scale(0.98);
}

.btn.primary {
  background: linear-gradient(135deg, #10b981, #059669);
  color: #fff;
  font-weight: 600;
}

.btn.wide {
  width: 100%;
  padding: 0.85rem;
  margin-top: 0.5rem;
}

.btn.ghost {
  background: transparent;
  border: 1px solid #475569;
  color: #cbd5e1;
}

.match-card {
  text-align: center;
  padding: 3rem 2rem;
}

.match-visual {
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.orbit span {
  display: inline-block;
  width: 12px;
  height: 12px;
  margin: 0 6px;
  border-radius: 50%;
  background: #10b981;
  animation: bounce 1.2s ease-in-out infinite;
}

.orbit span:nth-child(2) {
  animation-delay: 0.15s;
}

.orbit span:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  50% {
    transform: translateY(-10px);
    opacity: 1;
  }
}

.match-title {
  font-size: 1.15rem;
}

.match-meta {
  color: #64748b;
  font-size: 0.85rem;
}

.vs-preview {
  margin-top: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.25rem;
  flex-wrap: wrap;
}

.vs-you,
.vs-opp {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  background: #0f172a;
  border-radius: 12px;
}

.vs-you em,
.vs-opp em {
  font-style: normal;
  color: #6ee7b7;
  font-size: 0.85rem;
}

.dot.you {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
}

.vs {
  font-weight: 800;
  color: #475569;
}

.avatar-sm {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  color: #fff;
}

.room-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  font-size: 0.8rem;
  color: #64748b;
}

.room-dot {
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  background: #334155;
  font-size: 0.65rem;
  font-weight: 700;
  color: #94a3b8;
}

.room-dot.live {
  background: rgba(239, 68, 68, 0.2);
  color: #fca5a5;
}

.play-grid {
  display: grid;
  grid-template-columns: 260px 1fr 220px;
  gap: 1.25rem;
  align-items: start;
}

@media (max-width: 1024px) {
  .play-grid {
    grid-template-columns: 1fr;
  }
}

.panel {
  background: rgba(17, 24, 39, 0.88);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 1rem;
}

.player-row {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.75rem;
  border-radius: 12px;
  border: 2px solid transparent;
  margin-bottom: 0.5rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.player-row.active {
  border-color: rgba(16, 185, 129, 0.55);
  box-shadow: 0 0 24px rgba(16, 185, 129, 0.15);
}

.player-row.local .pname {
  color: #a7f3d0;
}

.avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.player-info {
  flex: 1;
  min-width: 0;
}

.pname {
  font-weight: 600;
  font-size: 0.95rem;
}

.chip {
  font-size: 0.65rem;
  padding: 0.1rem 0.35rem;
  border-radius: 4px;
  margin-left: 0.25rem;
  vertical-align: middle;
}

.chip.black {
  background: #1f2937;
}

.chip.white {
  background: #e2e8f0;
  color: #0f172a;
}

.chip.you {
  background: #10b981;
  color: #fff;
}

.pmeta {
  font-size: 0.72rem;
  color: #64748b;
  margin-top: 0.15rem;
}

.disc-count {
  font-size: 1.5rem;
  font-weight: 800;
}

.turn-banner {
  text-align: center;
  padding: 0.85rem 0.5rem;
  margin: 0.5rem 0;
  min-height: 2.5rem;
  font-size: 0.88rem;
  color: #cbd5e1;
  background: rgba(15, 23, 42, 0.6);
  border-radius: 10px;
}

.pulse-text {
  color: #6ee7b7;
  animation: pulse-opacity 1.2s ease-in-out infinite;
}

@keyframes pulse-opacity {
  50% {
    opacity: 0.5;
  }
}

.actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.btn.action {
  background: #1e293b;
  color: #e2e8f0;
  font-size: 0.82rem;
  padding: 0.5rem;
}

.btn.action.danger {
  background: rgba(239, 68, 68, 0.15);
  color: #fca5a5;
  border: 1px solid rgba(239, 68, 68, 0.35);
}

.btn.action.muted {
  grid-column: span 2;
}

.board-col {
  display: flex;
  justify-content: center;
  padding: 0.5rem 0;
}

.chat-head {
  font-size: 0.8rem;
  color: #64748b;
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.chat-log {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 420px;
  overflow-y: auto;
  font-size: 0.78rem;
  line-height: 1.5;
}

.chat-log li {
  margin-bottom: 0.65rem;
}

.chat-log .sys {
  color: #64748b;
}

.chat-log .opp {
  color: #a5b4fc;
}

.chat-log .me {
  color: #6ee7b7;
}

.end-card {
  text-align: center;
  max-width: 420px;
  margin: 0 auto;
}

.end-icon {
  font-size: 3rem;
  margin-bottom: 0.5rem;
}

.end-sub {
  color: #94a3b8;
}

.end-score {
  font-size: 2rem;
  font-weight: 800;
  margin: 1rem 0;
}

.rating-change {
  color: #cbd5e1;
  margin-bottom: 1.5rem;
}

.rating-change .up {
  color: #6ee7b7;
}

.rating-change .down {
  color: #fca5a5;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal {
  background: #111827;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  max-width: 360px;
  width: 100%;
}

.modal h3 {
  margin: 0 0 0.75rem;
}

.modal p {
  color: #94a3b8;
  margin: 0 0 1.25rem;
  line-height: 1.5;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
</style>